/**
 * SpherePerspectiveRow - Read-only display of a sphere with its assigned perspective sphere
 *
 * Displays: [Sphere Avatar + Name] | [Assigned Sphere Avatar + Name]
 * Defaults to showing the 'center_center' perspective assignment.
 */

import React from 'react';
import { Sphere } from 'types/global';
import SphereAvatar from '../sphere-avatar/sphere-avatar';
import './SpherePerspectiveRow.scss';

interface SpherePerspectiveRowProps {
  /** The main sphere to display */
  sphere: Sphere;
  /** Perspective system name - defaults to 'center_center', shows the assigned sphere */
  perspectiveSystemName?: string;
  /** Optional class name for custom styling */
  className?: string;
}

/**
 * Get the assigned sphere for a given perspective
 */
function getAssignedSphere(sphere: Sphere, perspectiveSystemName: string): Sphere | null {
  if (!sphere.assignedPerspectives) {
    return null;
  }

  const assignedPerspective = sphere.assignedPerspectives.find(
    (p) => p.perspectiveProperties.system_name === perspectiveSystemName
  );

  return assignedPerspective?.assigned?.[0]?.sphere ?? null;
}

/**
 * Get the image ID from a sphere, handling array format
 */
function getSphereImageId(sphere: Sphere | null): string | undefined {
  if (!sphere?.properties?.imageId) {
    return undefined;
  }

  if (Array.isArray(sphere.properties.imageId)) {
    return sphere.properties.imageId[0];
  }

  return sphere.properties.imageId;
}

const SpherePerspectiveRow: React.FC<SpherePerspectiveRowProps> = ({
  sphere,
  perspectiveSystemName = 'center_center',
  className = '',
}) => {
  const assignedSphere = getAssignedSphere(sphere, perspectiveSystemName);

  return (
    <div className={`sphere-perspective-row ${className}`}>
      {/* Main sphere column */}
      <div className="sphere-perspective-row__sphere">
        <SphereAvatar
          glyph={sphere.glyph}
          glyphColor={sphere.glyphColor}
          sphereId={sphere.id}
          sphereName={sphere.properties.name}
          imageId={getSphereImageId(sphere)}
          size="small"
        />
        <span className="sphere-perspective-row__name">
          {sphere.properties.name}
        </span>
      </div>

      {/* Assigned perspective sphere column */}
      <div className="sphere-perspective-row__assigned">
        {assignedSphere ? (
          <>
            <SphereAvatar
              glyph={assignedSphere.glyph}
              glyphColor={assignedSphere.glyphColor}
              sphereId={assignedSphere.id}
              sphereName={assignedSphere.properties.name}
              imageId={getSphereImageId(assignedSphere)}
              size="small"
            />
            <span className="sphere-perspective-row__name">
              {assignedSphere.properties.name}
            </span>
          </>
        ) : (
          <span className="sphere-perspective-row__empty">—</span>
        )}
      </div>
    </div>
  );
};

export default SpherePerspectiveRow;
